import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Linkedin, Loader2, Send } from "lucide-react";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a short note.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks for the note — I'll be in touch.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your note. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-[#F1F5F9] border border-slate-200 px-4 py-3 text-slateink placeholder:text-slate-400 focus:outline-none focus:border-violet focus:ring-2 focus:ring-violet/20 transition";

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact | SymbiCognitive"
        description="Have a problem, an idea, or a good question? SymbiCognitive welcomes thoughtful exchanges about AI, operations, and leadership. Currently in development."
        path="/contact"
      />
      <section className="bg-midnight pt-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-16">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Contact</span>
          <h1 className="font-display font-extrabold text-white text-4xl sm:text-5xl tracking-tight mt-4 max-w-3xl leading-[1.05]">Have a problem, an idea, or a good question?</h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            Some of the most interesting ideas begin with a conversation about something that ought to work better. I welcome thoughtful exchanges about AI, operations, leadership, and the challenges of running smaller organizations.
          </p>
        </div>
        <div className="gradient-divider h-1.5 w-full" />
      </section>

      <section className="bg-white py-24 sm:py-28" data-testid="contact-body">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="mb-12 rounded-2xl bg-amber/10 border border-amber/30 px-6 py-4" data-testid="contact-disclosure">
            <p className="text-slateink text-sm sm:text-base">
              <strong>Please note:</strong> SymbiCognitive is currently in development and is not accepting consulting engagements. This is an invitation to exchange ideas.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}>
              <h2 className="font-display font-bold text-slateink text-2xl sm:text-3xl tracking-tight">Send a note</h2>
              <p className="mt-4 text-lg text-[var(--slate-soft)] max-w-md">Use the form, or reach out directly — whichever suits you.</p>

              <div className="mt-10 space-y-5">
                <a href="mailto:chris.dean@symbicognitive.com" data-testid="contact-email-link" className="flex items-center gap-4 group">
                  <span className="h-11 w-11 rounded-xl bg-violet/10 text-violet grid place-items-center group-hover:bg-violet group-hover:text-white transition-colors"><Mail size={20} /></span>
                  <span className="text-slateink group-hover:text-violet transition-colors">chris.dean@symbicognitive.com</span>
                </a>
                <a href="tel:7153794991" data-testid="contact-phone-link" className="flex items-center gap-4 group">
                  <span className="h-11 w-11 rounded-xl bg-teal/10 text-teal grid place-items-center group-hover:bg-teal group-hover:text-white transition-colors"><Phone size={20} /></span>
                  <span className="text-slateink group-hover:text-teal transition-colors">715-379-4991</span>
                </a>
                <a href="https://linkedin.com/in/crdean" target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" className="flex items-center gap-4 group">
                  <span className="h-11 w-11 rounded-xl bg-slate-100 text-slateink grid place-items-center group-hover:bg-slateink group-hover:text-white transition-colors"><Linkedin size={20} /></span>
                  <span className="text-slateink group-hover:text-slate-600 transition-colors">linkedin.com/in/crdean</span>
                </a>
              </div>
              <p className="mt-8 text-sm text-slate-400">Chris Dean · SymbiCognitive · Wisconsin</p>
            </motion.div>

            <motion.form onSubmit={submit} data-testid="contact-form" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, delay: 0.1 }} className="rounded-3xl border border-slate-200 p-7 sm:p-9 bg-white shadow-xl shadow-slate-200/40">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slateink mb-2">Name</label>
                  <input data-testid="contact-form-name" value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slateink mb-2">Email</label>
                  <input data-testid="contact-form-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@company.com" />
                </div>
              </div>
              <div className="mt-5">
                <label className="block text-sm font-medium text-slateink mb-2">Company <span className="text-slate-400 font-normal">(optional)</span></label>
                <input data-testid="contact-form-company" value={form.company} onChange={update("company")} className={inputCls} placeholder="Company name" />
              </div>
              <div className="mt-5">
                <label className="block text-sm font-medium text-slateink mb-2">Your note</label>
                <textarea data-testid="contact-form-message" value={form.message} onChange={update("message")} rows={5} className={inputCls} placeholder="What's on your mind?" />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-form-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-midnight text-white font-semibold px-6 py-3.5 rounded-xl hover:bg-slate-800 transition-colors disabled:opacity-60">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? "Sending..." : "Send a Note"}
              </button>
            </motion.form>
          </div>
        </div>
      </section>
    </div>
  );
}
